import React from "react";

const GolfMatchDetails = ({ games, userNames, styles }) => {
  // Parse golf data if it's not already an object
  const golfData = typeof games === "string" ? JSON.parse(games) : games;

  // Handle different data structures - golf might be nested in arrays
  let firstGolfGame;
  if (Array.isArray(golfData)) {
    firstGolfGame = golfData[0];
  } else {
    firstGolfGame = golfData;
  }

  // Get player names using IDs
  const player1Name =
    firstGolfGame.player1?.playerId && userNames[firstGolfGame.player1.playerId]
      ? userNames[firstGolfGame.player1.playerId]
      : firstGolfGame.player1?.name || "Player 1";

  const player2Name =
    firstGolfGame.player2?.playerId && userNames[firstGolfGame.player2.playerId]
      ? userNames[firstGolfGame.player2.playerId]
      : firstGolfGame.player2?.name || "Player 2";

  // Helper function for score status
  const getScoreStatus = (strokes, par) => {
    if (!strokes || !par) return "-";
    const diff = strokes - par;
    if (diff === -2) return "Eagle 🦅";
    if (diff === -1) return "Birdie 🐦";
    if (diff === 0) return "Par ⛳";
    if (diff === 1) return "Bogey 😬";
    if (diff >= 2) return "Double+ Bogey 😩";
    return "-";
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      {/* Match Overview */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fa",
          padding: "clamp(15px, 4vw, 25px)",
          borderRadius: "12px",
          marginBottom: "clamp(15px, 4vw, 25px)",
          border: "2px solid #e9ecef",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div style={{ textAlign: "center", flex: 1, minWidth: "120px" }}>
          <h3
            style={{
              color: "#2c3e50",
              margin: "0 0 clamp(10px, 3vw, 15px) 0",
              fontSize: "clamp(16px, 3.5vw, 20px)",
            }}
          >
            {player1Name}
          </h3>
          <div
            style={{
              fontSize: "clamp(32px, 8vw, 48px)",
              fontWeight: "bold",
              color: firstGolfGame.winner === "player1" ? "#27ae60" : "#6c757d",
              marginBottom: "clamp(8px, 2vw, 10px)",
            }}
          >
            {firstGolfGame.player1?.total || 0}
          </div>
          <div
            style={{ fontSize: "clamp(12px, 2.5vw, 14px)", color: "#6c757d" }}
          >
            Total Strokes
          </div>
          {firstGolfGame.winner === "player1" && (
            <div
              style={{ fontSize: "clamp(20px, 4vw, 24px)", marginTop: "10px" }}
            >
              🏆
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: "clamp(20px, 5vw, 28px)",
            fontWeight: "bold",
            color: "#6c757d",
            flexDirection: "column",
            padding: "0 clamp(10px, 4vw, 30px)",
            minWidth: "100px",
          }}
        >
          <div>VS</div>
          <div
            style={{
              fontSize: "clamp(11px, 2.2vw, 14px)",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            {firstGolfGame.holes || 0} holes played
          </div>
        </div>

        <div style={{ textAlign: "center", flex: 1, minWidth: "120px" }}>
          <h3
            style={{
              color: "#2c3e50",
              margin: "0 0 clamp(10px, 3vw, 15px) 0",
              fontSize: "clamp(16px, 3.5vw, 20px)",
            }}
          >
            {player2Name}
          </h3>
          <div
            style={{
              fontSize: "clamp(32px, 8vw, 48px)",
              fontWeight: "bold",
              color: firstGolfGame.winner === "player2" ? "#27ae60" : "#6c757d",
              marginBottom: "clamp(8px, 2vw, 10px)",
            }}
          >
            {firstGolfGame.player2?.total || 0}
          </div>
          <div
            style={{ fontSize: "clamp(12px, 2.5vw, 14px)", color: "#6c757d" }}
          >
            Total Strokes
          </div>
          {firstGolfGame.winner === "player2" && (
            <div
              style={{ fontSize: "clamp(20px, 4vw, 24px)", marginTop: "10px" }}
            >
              🏆
            </div>
          )}
        </div>
      </div>

      {/* Match Result Summary */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "clamp(15px, 4vw, 20px)",
          borderRadius: "12px",
          marginBottom: "clamp(15px, 4vw, 25px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          textAlign: "center",
        }}
      >
        <h4
          style={{
            color: "#2c3e50",
            marginBottom: "clamp(12px, 3vw, 15px)",
            fontSize: "clamp(16px, 3.5vw, 20px)",
          }}
        >
          🏆 Match Result
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "clamp(12px, 3vw, 20px)",
            fontSize: "clamp(13px, 2.5vw, 15px)",
          }}
        >
          <div>
            <strong>Winner:</strong>{" "}
            <span
              style={{
                color: firstGolfGame.winner === "draw" ? "#6c757d" : "#27ae60",
                fontWeight: "bold",
                fontSize: "clamp(14px, 3vw, 16px)",
              }}
            >
              {firstGolfGame.winner === "draw"
                ? "Draw"
                : firstGolfGame.winner === "player1"
                ? player1Name
                : firstGolfGame.winner === "player2"
                ? player2Name
                : "In Progress"}
            </span>
          </div>
          <div>
            <strong>Holes Played:</strong> {firstGolfGame.holes || 0} / 18
          </div>
          <div>
            <strong>Game Mode:</strong> {firstGolfGame.gameMode || "Singles"}
          </div>
          <div>
            <strong>Stroke Difference:</strong>{" "}
            {Math.abs(
              (firstGolfGame.player1?.total || 0) -
                (firstGolfGame.player2?.total || 0)
            )}{" "}
            strokes
          </div>
        </div>
      </div>

      {/* Hole-by-Hole Details */}
      {firstGolfGame.holes > 0 && (
        <div style={{ marginBottom: "clamp(15px, 4vw, 20px)" }}>
          <h4
            style={{
              color: "#2c3e50",
              marginBottom: "clamp(12px, 3vw, 15px)",
              fontSize: "clamp(16px, 3.5vw, 20px)",
            }}
          >
            ⛳ Hole-by-Hole Scorecard
          </h4>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.setsTable}>
              <thead>
                <tr>
                  <th style={styles.tableTh}>Hole</th>
                  <th style={styles.tableTh}>Par</th>
                  <th style={styles.tableTh}>{player1Name}</th>
                  <th style={styles.tableTh}>Score</th>
                  <th style={styles.tableTh}>{player2Name}</th>
                  <th style={styles.tableTh}>Score</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: firstGolfGame.holes }, (_, i) => {
                  const hole = i + 1;
                  const par = firstGolfGame.pars?.[i] || 4;
                  const player1Strokes =
                    firstGolfGame.player1?.strokes?.[i] || 0;
                  const player2Strokes =
                    firstGolfGame.player2?.strokes?.[i] || 0;

                  const player1Status = getScoreStatus(player1Strokes, par);
                  const player2Status = getScoreStatus(player2Strokes, par);

                  // Determine hole winner
                  const holeWinner =
                    player1Strokes < player2Strokes
                      ? "player1"
                      : player2Strokes < player1Strokes
                      ? "player2"
                      : "tie";

                  return (
                    <tr
                      key={i}
                      style={{
                        backgroundColor:
                          holeWinner === "player1"
                            ? "#d4edda"
                            : holeWinner === "player2"
                            ? "#f8d7da"
                            : "transparent",
                      }}
                    >
                      <td style={styles.tableTd}>
                        <strong>{hole}</strong>
                      </td>
                      <td style={styles.tableTd}>{par}</td>
                      <td style={styles.tableTd}>
                        <strong>{player1Strokes}</strong>
                        {holeWinner === "player1" && " 🏆"}
                      </td>
                      <td style={styles.tableTd}>
                        <span
                          style={{
                            color:
                              player1Strokes <= par
                                ? "#27ae60"
                                : player1Strokes === par + 1
                                ? "#f39c12"
                                : "#e74c3c",
                            fontWeight: "500",
                          }}
                        >
                          {player1Status}
                        </span>
                      </td>
                      <td style={styles.tableTd}>
                        <strong>{player2Strokes}</strong>
                        {holeWinner === "player2" && " 🏆"}
                      </td>
                      <td style={styles.tableTd}>
                        <span
                          style={{
                            color:
                              player2Strokes <= par
                                ? "#27ae60"
                                : player2Strokes === par + 1
                                ? "#f39c12"
                                : "#e74c3c",
                            fontWeight: "500",
                          }}
                        >
                          {player2Status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Player Statistics */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "clamp(12px, 4vw, 20px)",
          marginTop: "clamp(15px, 4vw, 25px)",
        }}
      >
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "clamp(15px, 4vw, 20px)",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
          }}
        >
          <h4
            style={{
              color: "#2c3e50",
              marginBottom: "clamp(12px, 3vw, 15px)",
              textAlign: "center",
              fontSize: "clamp(15px, 3vw, 18px)",
            }}
          >
            📊 {player1Name} Performance
          </h4>
          <div
            style={{
              display: "grid",
              gap: "10px",
              fontSize: "clamp(13px, 2.5vw, 15px)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Strokes:</span>
              <strong>{firstGolfGame.player1?.total || 0}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Average per Hole:</span>
              <strong>
                {firstGolfGame.holes > 0
                  ? (
                      (firstGolfGame.player1?.total || 0) / firstGolfGame.holes
                    ).toFixed(1)
                  : "0.0"}
              </strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Best Hole:</span>
              <strong>
                {firstGolfGame.player1?.strokes
                  ? `${Math.min(
                      ...firstGolfGame.player1.strokes.filter((s) => s > 0)
                    )} strokes`
                  : "N/A"}
              </strong>
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "clamp(15px, 4vw, 20px)",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
          }}
        >
          <h4
            style={{
              color: "#2c3e50",
              marginBottom: "clamp(12px, 3vw, 15px)",
              textAlign: "center",
              fontSize: "clamp(15px, 3vw, 18px)",
            }}
          >
            📊 {player2Name} Performance
          </h4>
          <div
            style={{
              display: "grid",
              gap: "10px",
              fontSize: "clamp(13px, 2.5vw, 15px)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total Strokes:</span>
              <strong>{firstGolfGame.player2?.total || 0}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Average per Hole:</span>
              <strong>
                {firstGolfGame.holes > 0
                  ? (
                      (firstGolfGame.player2?.total || 0) / firstGolfGame.holes
                    ).toFixed(1)
                  : "0.0"}
              </strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Best Hole:</span>
              <strong>
                {firstGolfGame.player2?.strokes
                  ? `${Math.min(
                      ...firstGolfGame.player2.strokes.filter((s) => s > 0)
                    )} strokes`
                  : "N/A"}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* Golf Scoring Legend */}
      <div
        style={{
          backgroundColor: "#e6f7ff",
          padding: "clamp(12px, 3vw, 15px)",
          borderRadius: "8px",
          marginTop: "clamp(15px, 4vw, 20px)",
          fontSize: "clamp(12px, 2.5vw, 14px)",
          color: "#0070f3",
          border: "1px solid #91d5ff",
        }}
      >
        <strong>Golf Scoring Guide:</strong>
        <span style={{ marginLeft: "10px" }}>
          Eagle (-2) 🦅 | Birdie (-1) 🐦 | Par (0) ⛳ | Bogey (+1) 😬 | Double+
          Bogey (+2) 😩
        </span>
        <br />
        <strong>Objective:</strong> Complete each hole in as few strokes as
        possible. Lower total score wins.
      </div>
    </div>
  );
};

export default GolfMatchDetails;
